import { readCookie } from './client.js';
import { ProblemError } from './errors.js';

/** @param {string} text */
function parseJson(text) {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function xsrfToken() {
  const raw = readCookie('XSRF-TOKEN');
  if (!raw) return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/**
 * multipart POST with upload progress. `fetch` cannot report upload progress, so this uses XMLHttpRequest with the
 * same conventions as `client.request`: session cookie (`withCredentials`), `X-XSRF-TOKEN` (cookie fetched lazily,
 * one retry on 419), ProblemError (RFC 9457) for non-2xx. Without XMLHttpRequest (Node/tests) it falls back to
 * `client.post` (no progress events). Resolves with the full JSON body (e.g. `{ data, meta }`).
 * @param {import('./client.js').ApiClient} client
 * @param {string} path
 * @param {FormData} body
 * @param {{ onProgress?: (fraction: number) => void, signal?: AbortSignal }} [opts]
 */
export async function postWithProgress(client, path, body, opts = {}) {
  if (typeof XMLHttpRequest === 'undefined') {
    const result = await client.post(path, body, { signal: opts.signal });
    opts.onProgress?.(1);
    return result;
  }
  if (!xsrfToken()) await client.csrf();

  /** @returns {Promise<XMLHttpRequest>} */
  const send = () =>
    new Promise((resolve, reject) => {
      if (opts.signal?.aborted) {
        reject(opts.signal.reason ?? new DOMException('Aborted', 'AbortError'));
        return;
      }
      const xhr = new XMLHttpRequest();
      xhr.open('POST', client.url(path));
      xhr.withCredentials = true;
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
      const token = xsrfToken();
      if (token) xhr.setRequestHeader('X-XSRF-TOKEN', token);
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) opts.onProgress?.(event.loaded / event.total);
      };
      const onAbort = () => xhr.abort();
      opts.signal?.addEventListener('abort', onAbort, { once: true });
      xhr.onloadend = () => opts.signal?.removeEventListener('abort', onAbort);
      xhr.onload = () => resolve(xhr);
      xhr.onerror = () => reject(new ProblemError({ status: 0, type: 'network-error' }));
      xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
      xhr.send(body);
    });

  let xhr = await send();
  if (xhr.status === 419) {
    await client.csrf();
    xhr = await send();
  }
  const parsed = parseJson(xhr.responseText);
  if (xhr.status < 200 || xhr.status >= 300) {
    /** @type {Record<string, any>} */
    const problem = parsed && typeof parsed === 'object' ? { ...parsed } : {};
    if (!problem.detail && typeof problem.message === 'string') problem.detail = problem.message;
    throw new ProblemError({ ...problem, status: problem.status ?? xhr.status });
  }
  opts.onProgress?.(1);
  return parsed;
}
